import React from 'react';
import classNames from 'classnames';
import isMatch from 'lodash/isMatch';
import { Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from '@material-ui/core';
import { withStyles, createStyles, Theme, WithStyles } from '@material-ui/core/styles';
import { JetOrderOption } from '../../helpers/Query';
import { SparklineChart, PercentChart, BarChart, TimelineBarChart } from '../JetChart';
import { ColumnData, ColumnResult } from './ColumnData';
import { ColumnType } from './ColumnType';
import { RTRDisplay } from '../common/rtrDisplay';
import { firestoreStores } from '../../../common/helpers/Firestore';
import { shouldDisplayReadThroughRate } from '../../../common/helpers/Utils';
import { numericLocalFormat } from './tableCellFormatUtils';

const styles = (theme: Theme) => createStyles({
    root: {
        width: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        // probably to be able to show full overlay messages from cells
        paddingTop: 200,
        marginTop: -200
    },
    table: {
        width: '100%',
        left: 0,
        minWidth: 300
    },
    tableHeader: {
        whiteSpace: 'nowrap',
        padding: 14,
        boxSizing: 'border-box'
    },
    tableHeaderWithSort: {
        paddingLeft: 0
    },
    tableContainer: {
        width: '100%'
    },
    stickyContainer: {
        position: 'fixed',
        overflow: 'hidden',
        zIndex: 500,
        backgroundColor: 'white',
        [theme.breakpoints.down('xs')]: {
            top: '104px',
            paddingTop: '8px'
        }
    },
    tableStickyHeader: {
        tableLayout: 'fixed',
        whiteSpace: 'nowrap',
        position: 'relative'
    },
    link: {
        textDecoration: 'none',
        color: 'inherit',
        overflow: 'hidden',
        maxWidth: 320,
        textOverflow: 'ellipsis',
        display: 'inline-block',
        [theme.breakpoints.down('xs')]: {
            maxWidth: 160
        }
    },
    cell: {
        lineHeight: 'normal',
        position: 'relative'
    },
    markedCell: {
        fontWeight: 'bold'
    },
    title: {
        fontSize: 14,
        padding: '4px 14px 4px 14px'
    },
    sparkline: {
        padding: '8px 0'
    },
    timelineBarChart: {
        padding: 0
    },
    center: {
        textAlign: 'center',
        '& span': {
            paddingRight: 24
        }
    },
    stickyHeaderCell: {
        position: 'sticky',
        top: 0,
        backgroundColor: 'white !important',
        zIndex: 500
    },
    stickyBodyCell: {
        position: 'sticky',
        backgroundColor: '#fafafa',
        left: 0,
        zIndex: 100,
        '&:first-child::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            width: '2px',
            height: '100%',
            boxShadow: '6px 5px 15px 0px rgb(0 0 0 / 20%)'
        }
    },
    rtr: {
        width: '40px',
        margin: '0 auto'
    },
    percentChart: {},
    barChart: {}
});

export interface JetTableProperties extends WithStyles<typeof styles> {
    className?: string;
    columns: ColumnData[];
    rowsData: ColumnResult[][];
    sortBy: string;
    orderBy: JetOrderOption;
    onSortUpdate: (columnId: string, orderBy: JetOrderOption) => void;
    stickyThreshold: number;
}

interface JetTableState {
    showStickyHeader: boolean;
    contentWidth: number;
    tableWidth?: number;
    columnWidths: number[] | null;
    scrollLeft: number;
}

interface CellData extends ColumnResult {
    cellIndex: number;
}

class JetTableComponent extends React.Component<JetTableProperties, JetTableState> {

    private readonly classes: JetTableProperties['classes'];
    private readonly rootDivRef: React.RefObject<HTMLDivElement>;
    private readonly tableContainerRef: React.RefObject<HTMLDivElement>;
    private readonly isBetaUser: boolean;

    constructor(props: JetTableProperties) {
        super(props);
        this.classes = this.props.classes;
        this.handleSortClick = this.handleSortClick.bind(this);
        this.rootDivRef = React.createRef();
        this.tableContainerRef = React.createRef();
        this.state = {
            showStickyHeader: false,
            contentWidth: 300,
            columnWidths: null,
            scrollLeft: 0
        };

        this.isBetaUser = firestoreStores.userStore.getBetaStatus();
    }

    componentDidMount() {
        if (this.rootDivRef.current === null || window === null) {
            return;
        }
        window.addEventListener('scroll', this.handleScroll);
        window.addEventListener('resize', this.handleResize);
        this.rootDivRef.current.addEventListener('scroll', this.handleHorizontalScroll);
    }

    componentWillUnmount() {
        if (this.rootDivRef.current === null || window === null) {
            return;
        }
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
        this.rootDivRef.current.removeEventListener('scroll', this.handleHorizontalScroll);
    }

    handleHorizontalScroll = (e: Event) => {
        this.setState({
            scrollLeft: -(e.target as HTMLDivElement).scrollLeft
        });
    }

    handleScroll = () => {
        const tableNode = this.tableContainerRef.current;
        if (tableNode === null) {
            return; // table not loaded yet
        }
        const rect = tableNode.getBoundingClientRect();

        if (this.state.showStickyHeader !== (rect.y <= this.props.stickyThreshold)) {
            const selector = '#mainJetTable > thead > tr > th';
            const colWidths = Array.from(tableNode.querySelectorAll(selector)).map(
                e => e.clientWidth
            );
            const tableWidth = tableNode.querySelectorAll('#mainJetTable')[0].clientWidth;

            const newState = {
                showStickyHeader: rect.y <= this.props.stickyThreshold,
                contentWidth: rect.width,
                tableWidth: tableWidth,
                columnWidths: colWidths
            };
            if (!isMatch(this.state, newState)) {
                this.setState(newState);
            }
        }
    }

    handleResize = () => {
        const node = this.tableContainerRef.current;
        if (!node) {
            return;
        }
        const rect = node.getBoundingClientRect();
        this.setState({
            contentWidth: rect.width
        });
    }

    render() {
        return (
            <div className={classNames(this.classes.root, this.props.className)}
                ref={this.rootDivRef}
            >
                <div
                    ref={this.tableContainerRef}
                    className={this.classes.tableContainer}
                >
                    {this.getStickyHeaderContainer()}
                    <Table
                        id="mainJetTable"
                        className={this.classes.table}
                    >
                        {this.getTableHead()}
                        {this.getTableBody()}
                    </Table>
                </div>
            </div>
        );
    }

    getStickyHeaderContainer() {
        if (!this.state.showStickyHeader) {
            return null;
        }
        return (
            <div className={this.classes.stickyContainer}
                style={{
                    width: this.state.contentWidth,
                    top: this.props.stickyThreshold
                }}>
                {this.getStickyHeader()}
            </div>);
    }

    getStickyHeader() {
        return (
            <Table
                className={this.classes.tableStickyHeader}
                style={{
                    width: this.state.tableWidth,
                    left: this.state.scrollLeft
                }}
            >
                {this.getTableHead({ sticky: true })}
            </Table>
        );
    }

    getTableHead({ sticky = false } = {}) {
        const tableColumns = this.props.columns;
        return (
            <TableHead
                innerRef={sticky ? null : undefined} // this.tableHeadHandle was never defined, leftover?
            >
                <TableRow>
                    {tableColumns.map((column, index) => (
                        <TableCell
                            key={column.label}
                            align={column.type === ColumnType.NUMERIC ? 'right' : 'left'}
                            className={classNames(this.classes.tableHeader, {
                                [this.classes.tableHeaderWithSort]: column.isSortable,
                                [this.classes.center]: column.hasSparkline,
                                [this.classes.stickyHeaderCell]: index === 0 && sticky,
                                [this.classes.stickyBodyCell]: index === 0
                            })}
                            colSpan={
                                (column.id ? 1 : 0) +
                                (column.hasSparkline ? 1 : 0) +
                                (column.hasPercentChart ? 1 : 0) +
                                (column.hasBarChart ? 1 : 0) + (column.hasTimelineBarChart ? 1 : 0)
                            }
                            style={sticky && this.state.columnWidths ? { width: this.state.columnWidths[index] } : undefined}
                            data-testid={`table-header-${column.id}`}
                        >
                            {column.isSortable ? (
                                <TableSortLabel
                                    active={this.props.sortBy === column.effectiveId}
                                    direction={this.props.sortBy === column.effectiveId ? this.props.orderBy : this.getDefaultOrder(column.effectiveId)}
                                    onClick={this.handleSortClick.bind(this, column.effectiveId)}
                                >
                                    {column.label}
                                </TableSortLabel>
                            ) : column.label}
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
        );
    }

    getTableBody() {
        return (
            <TableBody>
                {this.props.rowsData.map((cells, index) => this.getTableRow(cells, index))}
            </TableBody>
        );
    }

    getTableRow(cells: ColumnResult[], rowIndex: number) {
        const children = [];
        for (let cellIndex = 0; cellIndex < cells.length; cellIndex++) {
            const cell = { ...cells[cellIndex], cellIndex };
            const column = this.props.columns[cellIndex];
            if (column.id && !column.hasRTR) {
                children.push(this.getTableCell(cell, column));
            }
            if (column.hasSparkline) {
                children.push(this.getSparklineCell(cell, column));
            }
            if (column.hasPercentChart) {
                children.push(this.getPercentChartCell(cell, column));
            }
            if (column.hasBarChart) {
                children.push(this.getBarChartCell(cell, column));
            }
            if (column.hasTimelineBarChart) {
                children.push(this.getTimelineBarChartCell(cell, column));
            }
            if (column.hasRTR) {
                children.push(this.getRTRCell(cell, column));
            }
        }
        return (
            <TableRow key={rowIndex}>
                {children}
            </TableRow>
        );
    }

    getTableCell({ data, relatedData, cellIndex }: CellData, column: ColumnData) {
        let content = column.postProcessor(String(data), relatedData as any);
        const customStyle = column.type === ColumnType.LINK ? this.classes.title : undefined;
        const isNumeric = column.type === ColumnType.NUMERIC;

        content = numericLocalFormat(content, column.type);

        return (
            <TableCell
                key={column.effectiveId}
                align={isNumeric ? 'right' : 'left'}
                className={classNames(this.classes.cell, customStyle, {
                    [this.classes.markedCell]: column.isHighlighted,
                    [this.classes.stickyBodyCell]: cellIndex === 0
                })}
            >
                {content}
            </TableCell>
        );
    }

    getSparklineCell({ sparkline = [], sparklineTimes, sharedMaxValues = {} }: CellData, column: ColumnData) {
        return (
            <TableCell
                key={column.id + 'Sparkline'}
                padding={'none'}
                className={classNames(this.classes.cell, this.classes.sparkline)}
            >
                <SparklineChart
                    data={sparkline}
                    times={sparklineTimes}
                    max={sharedMaxValues.sparkline}
                    style={column.sparklineStyle}
                />
            </TableCell>
        );
    }

    getPercentChartCell({ data, relatedData }: CellData, column: ColumnData) {
        const withTableCell = (percentChart: any) => (
            <TableCell
                key={column.id + 'Percent'}
                padding={'none'}
                className={classNames(this.classes.cell, this.classes.percentChart)}
            >
                {percentChart}
            </TableCell>
        );

        if (column.percentChartHideIfNotPremium && !relatedData.wasPremium) {
            return withTableCell(null);
        }
        let resultData: any = data;
        if (column.percentChartPostProcessor) {
            resultData = column.percentChartPostProcessor(String(data), relatedData as any);
        }
        return withTableCell(
            <PercentChart
                percent={Number(resultData)}
                style={column.percentChartStyle}
            />
        );
    }

    getBarChartCell(cell: CellData, column: ColumnData) {
        return (
            <TableCell
                key={column.id + 'Bar'}
                className={classNames(this.classes.cell, this.classes.barChart)}
            >
                <BarChart
                    label={column.label}
                    barOptions={column.barOptions}
                    data={cell}
                    style={column.barChartStyle}
                />
            </TableCell>
        );
    }

    getTimelineBarChartCell({ sumSparklines }: CellData, column: ColumnData) {
        return (
            <TableCell
                key={column.id + 'TimelineBar'}
                className={classNames(this.classes.cell, this.classes.timelineBarChart)}
            >
                <TimelineBarChart
                    settings={column.sumSparklines}
                    data={sumSparklines}
                    style={column.style}
                />
            </TableCell>
        );
    }

    handleSortClick(columnId: string) {
        let orderBy;
        if (this.props.sortBy === columnId) {
            orderBy = this.props.orderBy === JetOrderOption.ASC ? JetOrderOption.DESC : JetOrderOption.ASC;
        } else {
            orderBy = this.getDefaultOrder(columnId);
        }
        this.props.onSortUpdate(columnId, orderBy);
    }

    getDefaultOrder(columnId: string) {
        const column = this.props.columns.find(column => column.id === columnId);
        return column ? column.orderBy : JetOrderOption.DESC;
    }

    getRTRCell({ data, relatedData }: CellData, column: ColumnData) {
        const showRTR = shouldDisplayReadThroughRate(String(relatedData.articleType));

        return (
            <TableCell
                key={column.id + 'RTR'}
                className={classNames(this.classes.cell)}
            >
                <div className={this.classes.rtr}>
                    <RTRDisplay rtr={Number(data)} showRTR={showRTR}/>
                </div>
            </TableCell>
        );
    }
}

export const JetTable = withStyles(styles)(JetTableComponent);
